import React from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import { Typography, Box, Chip, Grid } from '@mui/material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const getUrgencyColor = (nivelUrgencia) => {
  const colores = {
    'ESI-1': '#FF4B4B', // Rojo
    'ESI-2': '#FF8F3E', // Naranja
    'ESI-3': '#FFB302', // Amarillo
    'ESI-4': '#4ADE80', // Verde
    'ESI-5': '#3B82F6', // Azul
  };
  return colores[nivelUrgencia] || '#757575';
};

const DetallesCaso = ({ caso }) => (
  <Box
    sx={{
      position: 'absolute',
      left: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      marginLeft: '12px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      padding: '16px',
      minWidth: '600px',
      maxWidth: '800px',
      zIndex: 1000,
      '&::before': {
        content: '""',
        position: 'absolute',
        left: '-8px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 0,
        height: 0,
        borderTop: '8px solid transparent',
        borderBottom: '8px solid transparent',
        borderRight: '8px solid white'
      }
    }}
  >
    <Grid container spacing={2}>
      {/* Columna Izquierda */}
      <Grid item xs={6}>
        {/* Nivel de Urgencia */}
        <Box mb={2}>
          <Typography variant="subtitle1" fontWeight="bold" color={getUrgencyColor(caso.nivelUrgencia)}>
            Nivel de Urgencia: {caso.nivelUrgencia}
          </Typography>
        </Box>

        {/* Diagnóstico Preliminar */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
            Diagnóstico Preliminar:
          </Typography>
          <Typography variant="body2">
            {caso.diagnosticoPreliminar}
          </Typography>
        </Box>

        {/* Síntomas */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
            Síntomas:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {caso.sintomas.map((sintoma, index) => (
              <Chip
                key={index}
                label={sintoma}
                size="small"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  fontSize: '0.75rem'
                }}
              />
            ))}
          </Box>
        </Box>
      </Grid>

      {/* Columna Derecha */}
      <Grid item xs={6}>
        {/* Signos de Alarma */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" color="error.main">
            Signos de Alarma:
          </Typography>
          <Box display="flex" flexDirection="column" gap={0.5}>
            {caso.signosAlarma.map((signo, index) => (
              <Typography key={index} variant="body2" color="error.main">
                • {signo}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Recomendaciones */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
            Recomendaciones:
          </Typography>
          <Box display="flex" flexDirection="column" gap={0.5}>
            {caso.recomendaciones.map((recomendacion, index) => (
              <Typography key={index} variant="body2">
                • {recomendacion}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Centro Recomendado */}
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
            Centro Recomendado:
          </Typography>
          <Typography variant="body2">
            {caso.centroRecomendado}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Box>
);

const TimelineConsultas = ({ casos = [] }) => {
  const casosArray = Array.isArray(casos) ? casos : [];
  
  if (casosArray.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary" align="center">
          No hay casos registrados para mostrar
        </Typography>
      </Box>
    );
  }

  // Ordenar casos por fecha
  const casosOrdenados = [...casosArray].sort((a, b) => {
    if (!a.fechaCreacion || !b.fechaCreacion) return 0;
    return new Date(b.fechaCreacion) - new Date(a.fechaCreacion);
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Historial de Casos
      </Typography>
      <Timeline>
        {casosOrdenados.map((caso, index) => {
          if (!caso) {
            console.warn('Caso inválido encontrado en el índice:', index);
            return null;
          }
          
          const fecha = caso.fechaCreacion ? 
            format(new Date(caso.fechaCreacion), "d 'de' MMMM", { locale: es }) :
            'Fecha no disponible';
            
          const hora = caso.fechaCreacion ?
            format(new Date(caso.fechaCreacion), "HH:mm", { locale: es }) :
            '';
          
          return (
            <TimelineItem key={caso._id || index}>
              <TimelineOppositeContent sx={{ flex: 0.2 }}>
                <Typography variant="caption" color="text.secondary">
                  {fecha}<br/>{hora}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <Box
                  sx={{
                    position: 'relative',
                    '&:hover': {
                      '& .caso-detalle': {
                        display: 'block',
                      }
                    }
                  }}
                >
                  <TimelineDot 
                    sx={{ 
                      bgcolor: getUrgencyColor(caso.nivelUrgencia || 'ESI-3'),
                      boxShadow: 2,
                      width: 12,
                      height: 12,
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'scale(1.1)',
                        transition: 'all 0.2s'
                      }
                    }} 
                  />
                  <Box
                    className="caso-detalle"
                    sx={{
                      display: 'none',
                      position: 'absolute',
                      left: '100%',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      marginLeft: '12px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      padding: '16px',
                      minWidth: '300px',
                      maxWidth: '500px',
                      zIndex: 1000,
                    }}
                  >
                    <DetallesCaso caso={caso} />
                  </Box>
                </Box>
                {index < casosOrdenados.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={caso.nivelUrgencia || 'No especificado'} 
                    size="small"
                    sx={{ 
                      bgcolor: getUrgencyColor(caso.nivelUrgencia || 'ESI-3'),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                  <Typography variant="body2">
                    {caso.tipo === 'audio' ? 'Análisis de Síntomas' : 
                     caso.tipo === 'examen' ? 'Análisis de Examen' : 
                     'Consulta'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {caso.diagnosticoPreliminar}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Box>
  );
};

export default TimelineConsultas; 